import {
  PriceBookHeader,
  TopSavings,
  SpendHighlights,
  KnowYourBenefits,
} from '@thd-olt-component-react/ocm_pd_component_myaccount-pricebook';
import { SpendMeters } from '@thd-olt-component-react/ocm_pd_component_myaccount-pricebook/dist/components/SpendMeters/SpendMeters';
import React, { useEffect, useRef } from 'react';
import { IPriceBookError } from '@thd-olt-component-react/ocm_pd_component_myaccount-pricebook/dist/types/errors';
import { getChannelID } from '../../utils/NetworkUtil';
import { getSvocId, getUserId } from '../../utils/user';
import { NationalAccount } from '../../components/PricingBenefits';
import { LegacyTiersProvider, RewardsAndOffersProvider } from '../../contexts';
import { useIsMobile, useRewardsAndOffers } from '../../hooks';
import { ERR_PRICING_BOOK } from '../../constants';
import { newrelic, PRICING_BOOK_PAGE_LOAD } from '../../utils/newrelic';

export const PricingBookV2Container: () => React.JSX.Element = () => {
  const { isMobile } = useIsMobile();
  const { rewardsAndOffers, generalError, setGeneralError } =
    useRewardsAndOffers();

  const errorCalledRef = useRef(false);

  const channelId = getChannelID();
  const userId = getUserId() ?? '';
  const customerAccountId = getSvocId() ?? '';

  // Update the function signature and usage:
  const handlePricingBookError = (error: IPriceBookError) => {
    errorCalledRef.current = true;

    newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
      result: 'error',
      errorMessage: error?.message || 'component error',
      version: 'v2',
    });
    if (generalError === null) {
      setGeneralError(ERR_PRICING_BOOK);
    }
  };

  useEffect(() => {
    // If no error was reported after initial render, log success
    if (!errorCalledRef.current && !generalError) {
      newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
        result: 'success',
        version: 'v2',
      });
    }
  }, [generalError]);

  return (
    <div>
      {!generalError ? (
        <div className="sui-gap-6 sui-flex sui-flex-col">
          <PriceBookHeader
            userId={userId}
            customerAccountId={customerAccountId}
            channelId={channelId}
            onError={handlePricingBookError}
            returnNullIfError
            disableAnalytics={false}
          />
          <div className="sui-w-full">
            <SpendMeters
              userId={userId}
              customerAccountId={customerAccountId}
              channelId={channelId}
              onError={handlePricingBookError}
              returnNullIfError
            />
          </div>
          <div className="sui-w-full">
            <SpendHighlights
              userId={userId}
              customerAccountId={customerAccountId}
              channelId={channelId}
              onError={handlePricingBookError}
              returnNullIfError
            />
          </div>
          <div className="sui-w-full">
            <TopSavings
              userId={userId}
              customerAccountId={customerAccountId}
              channelId={channelId}
              onError={handlePricingBookError}
              returnNullIfError
            />
          </div>
          <div className="sui-w-full sui-pt-2">
            <KnowYourBenefits
              userId={userId}
              customerAccountId={customerAccountId}
              channelId={channelId}
              onError={handlePricingBookError}
              returnNullIfError
            />
          </div>
          <span className="sui-border-solid sui-border-primary sui-border-b-1" />
          <RewardsAndOffersProvider>
            <LegacyTiersProvider rewardsAndOffers={rewardsAndOffers}>
              <NationalAccount
                isMobile={isMobile}
                nationalAccount={
                  rewardsAndOffers?.customerInfo?.nationalAccountDetails
                }
              />
            </LegacyTiersProvider>
          </RewardsAndOffersProvider>
        </div>
      ) : null}
    </div>
  );
};
